import { useCallback, useMemo } from 'react';
import type { FC } from 'react';
import { useTranslatableContentState } from '@application/state';
import { formatLanguages, useRouter } from '@application/utils';
import { Analytics } from '@application/utils/analytics';

import type { TranslateButtonProps } from './types';
import * as Styled from './styled';

export * from './types';

export const TranslateButton: FC<TranslateButtonProps> = (props) => {
  const { source } = props;

  const { translate, language } = useRouter();

  const { setTargetLang, cancelTranslation, targetLanguage, originalLanguage } =
    useTranslatableContentState();

  const shouldDisplay = useMemo(() => {
    return originalLanguage === undefined || language !== originalLanguage;
  }, [originalLanguage, language]);

  const isActive = useMemo(() => {
    return targetLanguage !== undefined && targetLanguage === language;
  }, [language, targetLanguage]);

  const label = useMemo(() => {
    if (isActive && targetLanguage) {
      const languageLabel = formatLanguages(language, [targetLanguage]) ?? '';
      return translate('common.buttons.translate.label.active').replace('$language', languageLabel);
    }
    if (!isActive && originalLanguage) {
      const languageLabel = formatLanguages(language, [originalLanguage]) ?? '';
      return translate('common.buttons.translate.label.inactive').replace(
        '$language',
        languageLabel,
      );
    }
    if (!isActive && !originalLanguage) {
      return translate('common.buttons.translate.label.inactiveNoLang');
    }

    return '';
  }, [isActive, language, originalLanguage, targetLanguage, translate]);

  const ctaLabel = useMemo(() => {
    if (isActive) {
      return translate('common.buttons.translate.cta.active');
    }
    return translate('common.buttons.translate.cta.inactive');
  }, [isActive, translate]);

  const onClick = useCallback(() => {
    if (isActive) {
      cancelTranslation();
    } else {
      setTargetLang(language);
      if (originalLanguage) {
        Analytics.clickOnTranslate(source, originalLanguage, language);
      }
    }
  }, [cancelTranslation, isActive, language, originalLanguage, setTargetLang, source]);

  if (!shouldDisplay) return null;
  return (
    <Styled.TranslateButtonContainer>
      {label}&nbsp;
      <Styled.TranslateButton onClick={onClick}>{ctaLabel}</Styled.TranslateButton>
    </Styled.TranslateButtonContainer>
  );
};
